import { bool, func, node, number, oneOf, oneOfType, string } from "prop-types";
import { createContext, forwardRef, useRef, useState } from "react";



import CardRelay from "./CardRelay";
import Container from "./Container";
import Contents from "./Contents";
import ExpandButton from "./ExpandButton";

const FnrAccordionContext = createContext();

/** Your friendly neighborhood accordion panel */
const FnrAccordion = forwardRef(
  /**
   * @param {object} param0
   * @param {string|import("react").ReactNode} param0.label - the text the should label the contents of the accordion
   * @param {boolean} param0.defaultClosed - if the accordion should start collasped, set this to true
   * @param {boolean} param0.active - use this to set the active state from the outside
   * @param {Function} param0.setActive - use this to control the active state from the outside
   * @param {boolean} param0.fullWidth - is forwarded to FnrHorizontalGap to right-align Fnrchevron
   * @param {string} param0.helperText - this text will be in a tooltip next to the label when included
   * @param {boolean} param0.dark - Makes accordion look good when placed on a dark background
   * @param {boolean} param0.alignContentToLabel - aligns accordion content with label when expanded. Use this when passing in text without using addCard.
   * @param {boolean} param0.noActiveMargin - Removes the margin added to opened accordions.
   * @param {boolean} param0.marginCorrection - Aligns label text instead of padding to parent element
   * @param {boolean} param0.isLoading - Adds loading bricks to the expander.
   * @param {boolean} param0.addCard - Wraps label with its contents with a border.
   * @param {boolean} param0.noHighlight - turns off background-color over effect
   * @param {"right"|"centered"|"left"|"none"} param0.chevronStyle - determines where to put the chevron. none will omit it so you can put a custom one into your label.
   * @param {string} param0.hoverBackgroundColor - overrides the default hoverBackgroundColor, but only when inactive or addCard set to false.
   * @param {string} param0.colorVariant - overrides default accordion colors
   * @param {string} param0.buttonBackgroundColor - sets the background color of the button
   * @param {string} param0.buttonBorder - sets the border of the button
   */
  (
    {
      label,
      defaultClosed,
      active: activeArg,
      setActive: setActiveArg,
      children,
      fullWidth,
      helperText,
      dark,
      alignContentToLabel,
      noActiveMargin,
      marginCorrection,
      isLoading,
      addCard,
      noHighlight,
      chevronStyle = "right",
      hoverBackgroundColor,
      colorVariant,
      buttonBackgroundColor,
      buttonBorder,
      ...rest
    },
    ref
  ) => {
    const buttonRef = useRef();
    const [active, setActive] = useState(!defaultClosed);

    const handleToggle = () => {
      if (setActiveArg) {
        setActiveArg(!activeArg);
      } else setActive(!active);
    };

    const theRealActive = typeof activeArg === "boolean" ? activeArg : active;
    const cardIsShowing = theRealActive && addCard;
    const noShade = noHighlight;

    return (
      <FnrAccordionContext.Provider
        value={{
          addCard,
          active: theRealActive,
          alignContentToLabel,
          buttonRef,
          cardIsShowing,
          chevronStyle,
          dark,
          helperText,
          fullWidth,
          handleToggle,
          hoverBackgroundColor,
          marginCorrection,
          isLoading,
          label,
          noActiveMargin,
          noShade,
          colorVariant,
          buttonBackgroundColor,
          buttonBorder,
        }}
      >
        <CardRelay>
          <Container ref={ref} {...rest}>
            <ExpandButton />
            <Contents>{children}</Contents>
          </Container>
        </CardRelay>
      </FnrAccordionContext.Provider>
    );
  }
);

FnrAccordion.propTypes = {
  label: oneOfType([string, node]),
  defaultClosed: bool,
  active: bool,
  setActive: func,
  alignContentToLabel: bool,
  fullWidth: bool,
  helperText: string,
  dark: bool,
  noActiveMargin: bool,
  marginCorrection: oneOfType([bool, number]),
  children: node,
  isLoading: bool,
  addCard: bool,
  noHighlight: bool,
  chevronStyle: oneOf(["right", "centered", "left", "none"]),
  hoverBackgroundColor: string,
  colorVariant: string,
  buttonBackgroundColor: string,
  buttonBorder: string,
};

export { FnrAccordionContext };
export default FnrAccordion;